/**
 * @fileoverview Reducto Jobs Admin Page
 *
 * Purpose: View and manage Reducto Extract jobs with cost tracking
 * and linkage to internal documents, jobs, organisations, and users.
 * URL: /admin/reducto
 * Protection: Requires super_admin role
 *
 * Dependencies:
 * - Convex: admin.reducto.listJobs, admin.reducto.cancelJob
 * - shadcn Table, Badge, Button, Card
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { convexQuery } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import {
  RefreshCw,
  XCircle,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Activity,
  Coins,
} from "lucide-react";
import { useState, useCallback } from "react";

export const Route = createFileRoute("/_app/_auth/admin/_layout/reducto")({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user?.isOwner) {
      throw redirect({ to: "/admin/master-library" });
    }
  },
  component: ReductoPage,
});

interface ReductoJob {
  jobId: string;
  status: string;
  type?: string;
  numPages?: number;
  totalPages?: number;
  createdAt?: string;
  progress?: number;
  credits?: number;
  estimatedCostUsd?: number;
  fileName?: string;
  jobName?: string;
  entityName?: string;
  orgName?: string;
  uploadedByEmail?: string;
  uploadedByName?: string;
}

interface JobsData {
  jobs: ReductoJob[];
  summary: {
    total: number;
    totalCredits: number;
    totalEstimatedCostUsd: number;
    byStatus: Record<string, number>;
  };
}

function ReductoPage() {
  const listJobs = useAction(api.admin.reducto.listJobs);
  const cancelJob = useAction(api.admin.reducto.cancelJob);

  const [data, setData] = useState<JobsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listJobs({});
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [listJobs]);

  const handleCancel = useCallback(
    async (jobId: string) => {
      setCanceling(jobId);
      try {
        const result = await cancelJob({ jobId });
        if (!result.success) {
          setError(`Cancel failed: ${result.error}`);
        }
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Cancel failed");
      } finally {
        setCanceling(null);
      }
    },
    [cancelJob, refresh],
  );

  const activeCount =
    (data?.summary.byStatus["Pending"] ?? 0) +
    (data?.summary.byStatus["InProgress"] ?? 0) +
    (data?.summary.byStatus["Completing"] ?? 0);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Reducto Jobs</h1>
          <p className="text-sm text-muted-foreground">
            Monitor extraction jobs, costs, and linked documents.
          </p>
        </div>
        <Button onClick={refresh} disabled={loading} variant="outline">
          <RefreshCw
            className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          {data ? "Refresh" : "Load Jobs"}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {data && (
        <div className="grid gap-4 md:grid-cols-5">
          <StatCard
            title="Total Jobs"
            value={String(data.summary.total)}
            icon={Activity}
          />
          <StatCard
            title="Active"
            value={String(activeCount)}
            icon={Clock}
            variant={activeCount > 0 ? "warning" : "default"}
          />
          <StatCard
            title="Completed"
            value={String(data.summary.byStatus["Completed"] ?? 0)}
            icon={CheckCircle2}
            variant="success"
          />
          <StatCard
            title="Failed"
            value={String(data.summary.byStatus["Failed"] ?? 0)}
            icon={AlertCircle}
            variant={
              (data.summary.byStatus["Failed"] ?? 0) > 0
                ? "destructive"
                : "default"
            }
          />
          <StatCard
            title="Total Cost"
            value={
              data.summary.totalEstimatedCostUsd > 0
                ? `$${data.summary.totalEstimatedCostUsd.toFixed(2)}`
                : "—"
            }
            subtitle={
              data.summary.totalCredits > 0
                ? `${data.summary.totalCredits.toLocaleString(undefined, { maximumFractionDigits: 1 })} credits`
                : undefined
            }
            icon={Coins}
          />
        </div>
      )}

      {data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            {data.jobs.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No Reducto jobs found.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead>Case / Org</TableHead>
                      <TableHead>Uploaded By</TableHead>
                      <TableHead className="text-right">Pages</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[60px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.jobs.map((job) => (
                      <TableRow key={job.jobId}>
                        <TableCell>
                          <span className="font-mono text-xs">{job.jobId}</span>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={job.status} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {job.type ?? "—"}
                        </TableCell>
                        <TableCell>
                          {job.fileName ? (
                            <span className="text-sm" title={job.fileName}>
                              {job.fileName}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {job.jobName || job.entityName || job.orgName ? (
                            <div className="flex flex-col">
                              {(job.jobName ?? job.entityName) && (
                                <span className="text-sm font-medium">
                                  {job.jobName ?? job.entityName}
                                </span>
                              )}
                              {job.orgName && (
                                <span className="text-xs text-muted-foreground">
                                  {job.orgName}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {job.uploadedByName || job.uploadedByEmail ? (
                            <div className="flex flex-col">
                              {job.uploadedByName && (
                                <span className="text-sm">
                                  {job.uploadedByName}
                                </span>
                              )}
                              {job.uploadedByEmail && (
                                <span className="text-xs text-muted-foreground">
                                  {job.uploadedByEmail}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          className={`text-right text-sm ${
                            (job.numPages ?? 0) > 20
                              ? "font-bold text-amber-600 dark:text-amber-400"
                              : ""
                          }`}
                        >
                          {job.numPages != null && job.totalPages != null
                            ? `${job.numPages}/${job.totalPages}`
                            : job.numPages != null
                              ? String(job.numPages)
                              : "—"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {job.credits != null
                            ? job.credits.toLocaleString(undefined, {
                                maximumFractionDigits: 1,
                              })
                            : "—"}
                        </TableCell>
                        <TableCell
                          className={`text-right font-mono text-sm ${
                            (job.estimatedCostUsd ?? 0) > 5
                              ? "font-bold text-red-600 dark:text-red-400"
                              : (job.estimatedCostUsd ?? 0) > 2
                                ? "text-amber-600 dark:text-amber-400"
                                : ""
                          }`}
                        >
                          {job.estimatedCostUsd != null
                            ? `$${job.estimatedCostUsd.toFixed(2)}`
                            : "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {job.createdAt ? formatDate(job.createdAt) : "—"}
                        </TableCell>
                        <TableCell>
                          {isActive(job.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancel(job.jobId)}
                              disabled={canceling === job.jobId}
                              className="h-8 w-8 p-0"
                            >
                              {canceling === job.jobId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <XCircle className="h-4 w-4 text-destructive" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!data && !loading && (
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed py-16">
          <Activity className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Click &ldquo;Load Jobs&rdquo; to fetch the current Reducto job
            queue.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const variants: Record<
    string,
    "default" | "secondary" | "destructive" | "outline"
  > = {
    Completed: "default",
    Failed: "destructive",
    Pending: "secondary",
    InProgress: "secondary",
    Completing: "secondary",
  };

  const icons: Record<string, typeof CheckCircle2 | undefined> = {
    Completed: CheckCircle2,
    Failed: AlertCircle,
    Pending: Clock,
    InProgress: Loader2,
  };

  const Icon = icons[status];

  return (
    <Badge variant={variants[status] ?? "outline"} className="gap-1">
      {Icon && (
        <Icon
          className={`h-3 w-3 ${status === "InProgress" ? "animate-spin" : ""}`}
        />
      )}
      {status}
    </Badge>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  variant = "default",
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: typeof Activity;
  variant?: "default" | "success" | "warning" | "destructive";
}) {
  const colorMap = {
    default: "text-muted-foreground",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    destructive: "text-destructive",
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`h-4 w-4 ${colorMap[variant]}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${colorMap[variant]}`}>{value}</div>
        {subtitle && (
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isActive(status: string): boolean {
  return ["Pending", "InProgress", "Idle", "Completing"].includes(status);
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
